/** Mockup light `:root` tokens — consume via `themeTokens.ts`. */
export const VIOLET = '#6758d9';
export const VIOLET_DARK = '#5145b5';
export const VIOLET_SOFT = '#f0efff';
export const AMBER = '#93370d';
export const AMBER_SOFT = '#fffaeb';
export const RED = '#b42318';
export const RED_SOFT = '#fef3f2';
export const GREEN = '#067647';
export const GREEN_SOFT = '#ecfdf3';
export const BLUE = '#175cd3';
export const BLUE_SOFT = '#eff8ff';
export const GRAY_SOFT = '#f2f4f7';
export const SURFACE_SOFT = '#fafbfc';
export const NEUTRAL_TEXT = '#475467';
export const STEP_LABEL = '#344054';
export const NEXT_STEP = '#f79009';
export const DONE_MARKER = '#12b76a';
export const SKIPPED_MARKER = '#98a2b3';

export const MUTED = '#667085';

/* -------------------------------------------------------------------------- */
/*                                  GLOBAL                                    */
/* -------------------------------------------------------------------------- */
export const TAB_LABEL = 'ID Verification';
export const LOADING_LABEL = 'Loading identity verification…';
export const ERROR_TITLE = 'Unable to load ID verification';
export const FETCH_ERROR = 'Error fetching ID verification';

/* -------------------------------------------------------------------------- */
/*                                  HEADER                                    */
/* -------------------------------------------------------------------------- */
export const HEADER_EYEBROW = 'Identity verification operations';
export const HEADER_TITLE = 'Attempts';
export const HEADER_SUBHEAD =
  'Each complete IDV upload submission creates one Attempt. Mark disposition when review is done.';

/* -------------------------------------------------------------------------- */
/*                                  EXCLUDE                                   */
/* -------------------------------------------------------------------------- */
export const EXCLUDE_LABEL = 'Exclude from auto-withdrawal';
export const EXCLUDE_HELPER = 'Skips reminders and timed withdrawal';
export const EXCLUDE_ERROR = 'Error updating auto-withdrawal exclusion';
export const DOCUMENT_LINK_UNAVAILABLE = 'Document link is unavailable.';

/* -------------------------------------------------------------------------- */
/*                                  BANNER                                    */
/* -------------------------------------------------------------------------- */
export const BANNER_ICON_SUCCESS = '✓';
export const BANNER_ICON_DEFAULT = '!';

export const BANNER_AI_TITLE = 'AI resolved identity review automatically';
export const BANNER_AI_COPY = (attemptId: string | number) =>
  `The AI pipeline re-processed Attempt #${attemptId} and auto-fulfilled identity verification. The BGC left ID Review with no agent action required.`;

export const BANNER_SOLVED_TITLE = (attemptId: string | number) =>
  `Identity review resolved from Attempt #${attemptId}`;
export const BANNER_SOLVED_LATEST_COPY =
  'Marked Solved — the BGC left ID Review. Reminders and auto-withdrawal no longer apply.';
export const BANNER_SOLVED_OLDER_COPY =
  'An earlier submission resolved the case. Later Attempts need no disposition. The BGC left ID Review.';

export const BANNER_DOCS_TITLE = 'Documents are not enough to resolve ID Review';
export const BANNER_DOCS_COPY = (attemptId: string | number) =>
  `Attempt #${attemptId} was marked Docs not enough. The BGC remains in ID Review and the withdrawal clock continues.`;

export const BANNER_UNATTENDED_TITLE = (attemptId: string | number) => `Attempt #${attemptId} is Unattended`;
export const BANNER_UNATTENDED_COPY = 'Choose a disposition and add a comment.';

export const BANNER_EXCLUDED_TITLE = 'Automatic withdrawal is disabled for this BGC';
export const BANNER_EXCLUDED_COPY =
  'Operations excluded this case. Reminder and withdraw steps will not run while excluded.';

export const BANNER_APPROACHING_TITLE = 'Auto-withdrawal is approaching';
export const BANNER_APPROACHING_COPY = (days: number) =>
  `Reminder 2 has already been sent. This BGC has ${days} day${days === 1 ? '' : 's'} left before Day 30 auto-withdrawal.`;
export const BANNER_APPROACHING_COPY_FALLBACK = 'Reminder 2 has already been sent.';

export const BANNER_WITHDRAWN_TITLE = 'BGC automatically withdrawn after 30 days in ID Review';
export const BANNER_WITHDRAWN_COPY =
  'Day 30 action completed. An internal Partner Worker note documents the withdrawal.';

export const BANNER_UNEXPECTED_EXIT_TITLE = 'This case left ID Review without a recorded outcome';
export const BANNER_UNEXPECTED_EXIT_COPY =
  'The BGC is no longer in identity review, but there is no Solved, AI resolution, exclusion, or auto-withdrawal on file. That is unexpected — do not treat identity as verified until you confirm how this status was set.';

export const BANNER_META_LATEST = 'latest submission';
export const BANNER_META_EARLIER = 'earlier submission';
export const BANNER_META_AI_FULFILL = 'AI auto-fulfill';

/* -------------------------------------------------------------------------- */
/*                                  POLICY                                    */
/* -------------------------------------------------------------------------- */
export const POLICY_EYEBROW = 'Partner policy';
export const POLICY_TITLE = 'ID Review reminders & auto-withdrawal';
export const POLICY_ADMIN_LABEL = 'View Partner IDV policy';
export const POLICY_ADMIN_PATH = '/admin/partneridvpolicy/';
export const POLICY_ENTERED_LABEL = 'Entered ID Review';
export const POLICY_STEP_LABELS = {
  reminder_1: 'Reminder 1',
  reminder_2: 'Reminder 2',
  auto_withdraw: 'Auto-withdraw',
} as const;
export const POLICY_SOURCE_DEFAULT = 'System default';
export const POLICY_SOURCE_OVERRIDE = (partnerName: string) => `${partnerName} override`;
export const POLICY_SOURCE_OVERRIDE_FALLBACK = 'Partner override';
export const POLICY_SKIP_AI = 'Skipped — AI resolved';
export const POLICY_SKIP_RESOLVED = 'Skipped — case resolved';
export const POLICY_SKIP_EXCLUDED = 'Skipped — BGC excluded';
export const POLICY_SKIP_GENERIC = 'Skipped — Not applicable';

/* -------------------------------------------------------------------------- */
/*                                 ATTEMPTS                                   */
/* -------------------------------------------------------------------------- */
export const ATTEMPTS_EYEBROW = 'Upload submissions';
export const MARK_AS_LABEL = 'Mark as';
export const AI_BADGE_LABEL = 'AI';
export const AI_BADGE_TITLE = 'AI path ran for this Attempt';
export const DISPOSITION_UNATTENDED = 'Unattended';
export const DISPOSITION_SOLVED = 'Solved';
export const DISPOSITION_DOCS_NOT_ENOUGH = 'Docs not enough';
export const DISPOSITION_OTHER = 'Other';
export const DISPOSITION_AI_RESOLVED = 'AI resolved';
export const RESOLVE_FLAG = 'Resolved the case';
export const RESOLVE_FLAG_AI = 'AI resolved the case';
export const SUBMITTED_PREFIX = 'Submitted ';
export const TAGGED_BY = (name: string) => `Tagged by ${name}`;
export const TAGGED_LABEL = 'Tagged';
export const AI_NOTE_ACTOR_FALLBACK = 'AI IDV';
export const AI_NOTE_SUFFIX = 'identity verification fulfilled. No agent disposition required.';
export const AI_NOTE = (actor: string, when: string) =>
  when ? `Auto-resolved by ${actor} · ${when} — ${AI_NOTE_SUFFIX}` : `Auto-resolved by ${actor} — ${AI_NOTE_SUFFIX}`;
export const NOTE_WAITING = 'System-created when the upload submission completed. Waiting for agent disposition.';
export const NOTE_EARLIER_RESOLVED =
  'System-created when the upload submission completed. No disposition needed — an earlier Attempt resolved the case.';
export const DOCUMENT_NOUN_ONE = 'document';
export const DOCUMENT_NOUN_MANY = 'documents';
export const DOC_THUMB_ID = 'ID';
export const DOC_THUMB_SSN = 'SSN';
export const DOC_THUMB_UB = 'UB';

/* -------------------------------------------------------------------------- */
/*                                  HISTORY                                   */
/* -------------------------------------------------------------------------- */
export const HISTORY_EYEBROW = 'Audit trail';
export const HISTORY_TITLE = 'Identity review history';
export const HISTORY_NOTES_LABEL = 'View Partner Worker notes';
export const HISTORY_EMPTY = 'No events match the current filters.';
export const HISTORY_FILTER_EYEBROW = 'Filter';
export const HISTORY_FILTER_SEARCH_PLACEHOLDER = 'Search values…';
export const HISTORY_FILTER_SELECT_ALL = '(Select all)';
export const HISTORY_FILTER_CLEAR_COLUMN = 'Clear this filter';
export const HISTORY_FILTER_DONE = 'Done';
export const HISTORY_FILTER_CLEAR_ALL = 'Clear all filters';
export const HISTORY_FILTER_CLOSE_LABEL = 'Close';
export const HISTORY_FILTER_NONE = 'none';
export const HISTORY_FILTER_VALUES = (count: number) => `${count} values`;
export const HISTORY_FILTER_CLEAR_LABEL = (column: string) => `Clear ${column} filter`;
export const HISTORY_COLUMNS = {
  time: 'Time',
  event: 'Event',
  actor: 'Actor',
  details: 'Details',
  attempt: 'Attempt',
} as const;
export const HISTORY_CASE_LABEL = 'Case';
export const HISTORY_AI_LABEL = 'AI';
export const HISTORY_EVENT_LABELS: Record<string, string> = {
  attempt_created: 'Attempt created',
  auto_withdraw_disabled: 'Auto-withdraw disabled',
  auto_withdraw_enabled: 'Auto-withdraw re-enabled',
  bgc_auto_withdrawn: 'BGC auto-withdrawn',
  case_resolved: 'Case resolved',
  disposition_changed: 'Disposition changed',
  entered_id_review: 'Entered ID Review',
  exited_id_review: 'Exited ID Review',
  reminder_1_sent: 'Reminder 1 sent',
  reminder_2_sent: 'Reminder 2 sent',
  resolution_undone: 'Resolution undone',
  validation_pass: 'Validation passed',
  name_verification_mismatch: 'Name verification mismatch',
  requires_manual_intervention: 'Manual intervention required',
  records_found: 'Public records found',
  records_not_found: 'Records not found',
  fulfill_identity_verification_started: 'Fulfillment started',
  ai_document_recognition_finished: 'AI recognition finished',
};

/* -------------------------------------------------------------------------- */
/*                                  MARK AS                                   */
/* -------------------------------------------------------------------------- */
export const MARK_AS_TITLE = (attemptId: string | number) => `Attempt #${attemptId}`;
export const MARK_AS_DISPOSITION_LABEL = 'Mark as';
export const MARK_AS_SELECT_LABEL = 'Select…';
export const MARK_AS_SOLVED_OPTION = 'Solved — resolves the case';
export const MARK_AS_COMMENT_LABEL = 'Comment';
export const MARK_AS_REQUIRED_LABEL = 'Required';
export const MARK_AS_COMMENT_PLACEHOLDER = 'Add an internal comment';
export const MARK_AS_SAVE_LABEL = 'Save';
export const MARK_AS_CANCEL_LABEL = 'Cancel';
export const MARK_AS_CLOSE_LABEL = 'Close';
export const MARK_AS_ERROR = 'Error updating attempt disposition';
export const MARK_AS_SUCCESS = 'Attempt disposition updated';
